import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Modal,
  TouchableOpacity,
  Image,
  FlatList,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';

interface Account {
  did: string;
  handle: string;
  displayName?: string;
  avatar?: string;
}

interface AccountSwitcherModalProps {
  visible: boolean;
  onClose: () => void;
  currentAccount?: Account;
  accounts: Account[];
  onSelectAccount: (account: Account) => void;
  onAddAccount: () => void;
}

export const AccountSwitcherModal: React.FC<AccountSwitcherModalProps> = ({
  visible,
  onClose,
  currentAccount,
  accounts,
  onSelectAccount,
  onAddAccount,
}) => {
  const renderAccount = ({ item }: { item: Account }) => {
    const isCurrent = item.did === currentAccount?.did;
    
    return (
      <TouchableOpacity
        style={[styles.accountItem, isCurrent && styles.currentAccountItem]}
        onPress={() => {
          if (!isCurrent) {
            onSelectAccount(item);
          }
          onClose();
        }}
        disabled={isCurrent}
      >
        {item.avatar ? (
          <Image source={{ uri: item.avatar }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatar, styles.avatarPlaceholder]}>
            <Ionicons name="person" size={24} color="#888" />
          </View>
        )}
        <View style={styles.accountInfo}>
          <Text style={styles.displayName}>
            {item.displayName || item.handle}
          </Text>
          <Text style={styles.handle}>@{item.handle}</Text>
        </View>
        {isCurrent && (
          <Ionicons name="checkmark-circle" size={24} color="#1d9bf0" />
        )}
      </TouchableOpacity>
    );
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <TouchableOpacity
        style={styles.overlay}
        activeOpacity={1}
        onPress={onClose}
      >
        <TouchableOpacity
          style={styles.modalContainer}
          activeOpacity={1}
          onPress={(e) => e.stopPropagation()}
        >
          <View style={styles.header}>
            <Text style={styles.headerTitle}>アカウント切り替え</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <Ionicons name="close" size={24} color="#fff" />
            </TouchableOpacity>
          </View>

          <FlatList
            data={accounts}
            renderItem={renderAccount}
            keyExtractor={(item) => item.did}
            contentContainerStyle={styles.listContent}
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>
                  保存されたアカウントはありません
                </Text>
              </View>
            }
          />

          <TouchableOpacity
            style={styles.addAccountButton}
            onPress={() => {
              onAddAccount();
              onClose();
            }}
          >
            <Ionicons name="add-circle-outline" size={24} color="#1d9bf0" />
            <Text style={styles.addAccountText}>アカウントを追加</Text>
          </TouchableOpacity>
        </TouchableOpacity>
      </TouchableOpacity>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#1a1a1a',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '70%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#fff',
  },
  closeButton: {
    padding: 4,
  },
  listContent: {
    padding: 8,
  },
  accountItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 12,
    borderRadius: 8,
    marginVertical: 4,
  },
  currentAccountItem: {
    backgroundColor: '#2a2a2a',
  },
  avatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
  },
  avatarPlaceholder: {
    backgroundColor: '#333',
    alignItems: 'center',
    justifyContent: 'center',
  },
  accountInfo: {
    flex: 1,
    marginLeft: 12,
  },
  displayName: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
    marginBottom: 2,
  },
  handle: {
    fontSize: 14,
    color: '#888',
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#888',
  },
  addAccountButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 16,
    borderTopWidth: 1,
    borderTopColor: '#333',
  },
  addAccountText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1d9bf0',
    marginLeft: 8,
  },
});
